/* eslint-disable max-lines */
import React from 'react';
import {sendEvent} from '@optimaxdev/analytics/mobile';

import {COMMON_ROUTES} from 'constants/router/common-routes';
import {QuickStartGuideLink} from 'components/startGuideLink/quickStartGuideLink';
import flags from 'constants/flags/flags';

import {OrderTracking} from './helpers/orderTracking/orderTracking';
import {AccountActionsConnected} from './helpers/accountActions/accountActions';
import {SearchConnected} from './helpers/search/search';
import {
    item,
    link,
    list,
    brand,
    disabledLink,
    singleLink,
    StructureItemType,
} from './helpers/structure/structure';
import {matchCategory} from './__utils/matchCategory';

/**
 * Get glasses brands
 * @param {string} type - Type of glasses
 * @returns {Array<StructureItemType>} Glasses brands
 */
export const brandsFactory = (type?: string): Array<StructureItemType> => {
    const eyewearType = type ? `?eyewear_type=${type}` : '';

    return [
        brand(matchCategory(`/ray-ban${eyewearType}`), 'rayBan'),
        brand(matchCategory(`/oakley-glasses${eyewearType}`), 'oakley'),
        brand(matchCategory(`/coach-eyewear${eyewearType}`), 'coach'),
        brand(matchCategory(`/michael-kors-eyewear${eyewearType}`), 'michaelKors'),
        brand(matchCategory(`/versace-eyewear${eyewearType}`), 'versace'),
        brand(matchCategory(`/hilaryduff${eyewearType}`), 'museAndHilaryDuff'),
        brand(matchCategory(`/costa-del-mar-sunglasses${eyewearType}`), 'costa'),
        brand(matchCategory(`/muse-eyewear${eyewearType}`), 'muse'),
        brand(matchCategory(`/ottoto${eyewearType}`), 'ottoto'),
    ];
};

const lensesBrands = [
    link(matchCategory('/contact-lenses?brand=acuvue'), 'Acuvue'),
    link(matchCategory('/contact-lenses?brand=air_optix'), 'Air Optix'),
    link(matchCategory('/contact-lenses?brand=biofinity'), 'Biofinity'),
    link(matchCategory('/contact-lenses?brand=dailies'), 'Dailies'),
    link(matchCategory('/contact-lenses?brand=freshlook'), 'FreshLook'),
    link(matchCategory('/contact-lenses?brand=proclear'), 'Proclear'),
    link(matchCategory('/contact-lenses?brand=soflens'), 'Soflens'),
    link(matchCategory('/contact-lenses?brand=clariti'), 'Clariti'),
];

type StructureItemObjectType = {
    path: string;
    name: string;
};

/**
 * Get structure item
 * @param {StructureItemObjectType} itemObj - props of item
 * @returns {Maybe<StructureItemType>} structure item
 */
export const structureItem = (itemObj: StructureItemObjectType): Maybe<StructureItemType> =>
    link(matchCategory(itemObj.path), itemObj.name);
/**
 * Get structure
 * @param {string} name - structure name
 * @param {Maybe<StructureItemType>[]} items - list ot items
 * @returns {StructureItemType} structure
 */
export const createStructure = (
    name: string,
    items: Maybe<StructureItemType>[],
): ReturnType<typeof list> => {
    return list(name, items.filter(Boolean));
};

export const EyeglassesStructure = createStructure('Eyeglasses', [
    structureItem({
        path: '/womens-eyeglasses',
        name: "Women's Eyeglasses",
    }),
    structureItem({
        path: '/mens-eyeglasses',
        name: "Men's Eyeglasses",
    }),
    structureItem({
        path: '/kids-glasses?tab=0&frame=2&p=1&eyewear_type=210',
        name: 'Kids Eyeglasses',
    }),
    structureItem({
        path: '/progressive-lenses',
        name: 'Multifocal',
    }),
    structureItem({
        path: '/eyeglasses-collection?p=1&product_badge=127&eyewear_type=210',
        name: 'Designer Eyeglasses',
    }),
    structureItem({
        path: '/ray-ban?eyewear_type=eyeglasses',
        name: 'Ray-Ban',
    }),
    structureItem({
        path: '/kids-glasses',
        name: 'Kids Glasses',
    }),
    structureItem({
        path: '/discounted-eyeglasses?eyewear_type=eyeglasses',
        name: 'Clearance Sale',
    }),
]);

export const SunglassesStructure = createStructure('Sunglasses', [
    structureItem({
        path: '/prescription-sunglasses',
        name: 'Prescription Sunglasses',
    }),
    structureItem({
        path: '/mens-sunglasses',
        name: "Men's Sunglasses",
    }),
    structureItem({
        path: '/womens-sunglasses',
        name: "Women's Sunglasses",
    }),
    structureItem({
        path: '/kids-glasses?tab=0&frame=2&p=1&eyewear_type=211',
        name: 'Kids Sunglasses',
    }),
    structureItem({
        path: '/polarized-sunglasses',
        name: 'Polarized Sunglasses',
    }),
    structureItem({
        path: '/ray-ban-sunglasses',
        name: 'Ray-Ban Sunglasses',
    }),
    structureItem({
        path: '/oakley-sunglasses',
        name: 'Oakley Sunglasses',
    }),
    structureItem({
        path: '/discounted-eyeglasses?eyewear_type=sunglasses',
        name: 'Sunglasses Sale',
    }),
]);

export const ClearanceStructure = list('Clearance', [
    link(matchCategory('/discounted-eyeglasses?eyewear_type=eyeglasses'), 'Eyeglasses clearance'),
    link(matchCategory('/discounted-eyeglasses?eyewear_type=sunglasses'), 'Sunglasses clearance'),
    link(matchCategory('/eyes-of-faith'), 'Designer Outlet'),
]);

export const MultifocalStructure = list('Multifocal', [
    link(matchCategory('/progressive-lenses?gender=women'), 'Women'),
    link(matchCategory('/progressive-lenses?gender=men'), 'Men'),
    link('/transitions-photochromic-lenses?tab=0&frame=2', 'Transitions® Lenses'),
    link(matchCategory('/prescription-sunglasses?lenstype=bifocal_progressive'), 'Rx. Sunglasses'),
]);

export const BrandsStructure = list('Brands', [
    link(matchCategory('/designer-glasses'), 'All brands'),
    link(matchCategory('/eyes-of-faith'), 'Designer Outlet'),
    ...brandsFactory(),
]);

export const ContactLensesStructure = list('Contact Lenses', [
    link(matchCategory('/contact-lenses'), 'All contacts brands'),
    ...lensesBrands,
]);

export const ExploreStructure = list('Explore', [
    link(matchCategory('/sports-glasses'), 'Sport glasses'),
    link(matchCategory('/computer-glasses'), 'Digital Block'),
    link('/scan?situation=mm', 'Prescription Scanner App'),
]);

export const VisionInsuranceLink = singleLink({
    to: '/vision-insurance',
    content: 'Vision Insurance',
    className: 'bold',
});

export const PrescriptionScannerLink = singleLink({
    to: '/scan?from=mMenu',
    content: (
        <span>
            <strong>Prescription</strong> Scanner
        </span>
    ),
    className: 'halfBold',
    analyticsData: {buttonName: 'Prescription Scanner'},
});

export const BlueLightLink = singleLink({
    to: '/blue-light-blocking-glasses?q=blue%20light&frame=1&tab=0',
    content: 'Blue Light',
    className: 'bold',
});

export const HelpCenterLink = singleLink({
    to: COMMON_ROUTES.HELP_CENTER,
    content: 'Help Center',
    className: 'grey',
    icon: 'helpCenter',
    analyticsData: {buttonName: 'Help', label: 'Help Center'},
});

const filterMenuItemsByFeatureFlags = <F extends ReturnType<typeof singleLink>>(
    menuItems: F[],
): F[] =>
    menuItems.filter(menuItem => {
        switch (menuItem) {
            case PrescriptionScannerLink:
                return !flags.prescriptionScanerOff.isEnabled();
            default:
                return true;
        }
    });

export const defaultStructure = filterMenuItemsByFeatureFlags([
    item(SearchConnected, {
        onSearch(query: string) {
            if (query !== '') {
                sendEvent('event', {
                    name: 'Keyword Search',
                    properties: {
                        dyType: 'keyword-search-v1',
                        keywords: query,
                    },
                });
            }
        },
    }),
    item(AccountActionsConnected),
    item(QuickStartGuideLink),
    EyeglassesStructure,
    SunglassesStructure,
    ClearanceStructure,
    MultifocalStructure,
    BrandsStructure,
    ContactLensesStructure,
    ExploreStructure,
    VisionInsuranceLink,
    PrescriptionScannerLink,
    BlueLightLink,
    item(OrderTracking),
    disabledLink(),
    HelpCenterLink,
]);
